import {getPolygonDimensions, projectTo2DFeetInches} from "./DxfCalculations";
import {AreasTable, BaselineDetailsTable, ObstacleDetailTable, SegmentDetailsTable, TotalObsTable} from "./DxfTables";
import {addFreeLabel, addMeasurementLable, addNameLabel, addPointLabels, SegmentDetailLabels} from "./DxfLabels";
import { buildBaselineMeasurementData, feetPointToInches, formatBaselineDistance } from "./DxfBaselineDimensions";
import {
    Colors,
    LWPolylineFlags,
    point2d,
    point3d
} from "@tarikjabiri/dxf";

const EDGE_DIMENSIONS_LAYER = "Edge Dimensions";
const BASELINE_DIMENSIONS_LAYER = "Edge Dimensions - Baseline";

export function drawEdge2dShapes(dxf, item, Block, i, layerName) {
    Block.setLayerName(layerName);

    let obsLabelIndex = 0;

    // Draw the main polygon in 2D inches and get converted points
    const polygon2DInches = drawShapeToBlock(dxf, item.rotated_polygon, Block, {
        addPointLabel: true,
        addMeasurementLabel: true,
        addCircle: true,
        shape: "polygon",
        layerName,
    }, i);

    // Do not serialize a partial polygon. AutoCAD rejects non-finite or
    // under-defined LWPOLYLINE geometry that some browser viewers tolerate.
    if (polygon2DInches.length < 3) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0, headerAnchorY: 0 };
    }

    // Draw dormers as obstacles
    item.dormerData.forEach((data, index) => {
        drawShapeToBlock(dxf, data, Block, {
            isObs: true,
            addName: true,
            addCircle: true,
            shape: "dormer",
            layerName,
        }, obsLabelIndex, i);
        obsLabelIndex++;
    });

    // Draw setbacks
    if (item.setbacks.length > 0) {
        drawShapeToBlock(dxf, item.rotated_setbacks, Block, {
            shape: "setback",
            layerName,
        }, obsLabelIndex, i);
        obsLabelIndex++;
    }

    // Draw obstacles
    if (item.obstruction.length > 0) {
        item.rotated_obstruction.forEach((obs, obsIndex) => {
            drawShapeToBlock(dxf, obs.polygon, Block, {
                isObs: true,
                addName: true,
                shape: "obstacle",
                layerName,
            }, obsLabelIndex, i);
            obsLabelIndex++;
        });
    }

    // Draw panels
    if (item.panels.length > 0) {
        item.rotated_panels.forEach((panel, panIndex) => {
            drawShapeToBlock(dxf, panel.polygon, Block, {
                shape: "panel",
                layerName,
            }, obsLabelIndex, i);
            obsLabelIndex++;
        });
    }

    let polyData = getPolygonDimensions(polygon2DInches);
    const tableBounds = drawTables(
        item,
        item.rotated_polygon,
        item.rotated_obstruction,
        item.dormerData,
        item.rotated_panels,
        polyData,
        Block,
        { layerName }
    );
    return mergeBounds(polyData, tableBounds);
}

// Draws data tables related to the polygon and its components
export function drawTables(item, reset2dPolygon, obsPoints, dormerPoints, panelPoints, polyData, Block, options = {}) {
    let cellHeight = 12;
    let tableStartY = polyData.minY - 50;
    let tableStartX = polyData.minX;
    let segmentColumns = [23, 25, 25];
    const segmentTableWidth = segmentColumns.reduce((sum, width) => sum + width, 0);
    const areaTableWidths = [50, 55, 57];
    const areaTableWidth = areaTableWidths.reduce((sum, width) => sum + width, 0);
    const obstacleColumns = [55, 52, 42, 30, 30];
    const obstacleTableWidth = obstacleColumns.reduce((sum, width) => sum + width, 0);
    const totalObsWidths = [88, 43];
    const totalObsWidth = totalObsWidths.reduce((sum, width) => sum + width, 0);

    // Draw the segment details table and labels
    let [xSpace, numRows] = SegmentDetailsTable(item.label, reset2dPolygon, tableStartX, tableStartY, cellHeight, segmentColumns, Block);
    const headerAnchorY = getSegmentHeaderAnchorY(item, polyData.maxY + 40);
    SegmentDetailLabels(Block, tableStartX, polyData.maxY + 40, item.detail, item.label);

    xSpace += tableStartX;

    // Draw area details table and track max rows
    numRows = Math.max(numRows, AreasTable(item.area_details, xSpace, tableStartY, cellHeight, areaTableWidths, Block));
    let ySpace = tableStartY - (numRows * cellHeight) - 12;
    let minY = tableStartY - ((numRows - 1) * cellHeight);
    let maxX = Math.max(
        tableStartX + segmentTableWidth,
        xSpace + areaTableWidth
    );

    // Draw obstacle and total obstacle tables if applicable
    if (item.obstruction.length > 0 || dormerPoints.length > 0) {
        const obstacleOffset = ObstacleDetailTable(
            item.label,
            [item.obstruction, obsPoints, dormerPoints],
            tableStartX,
            ySpace,
            cellHeight,
            obstacleColumns,
            Block
        );
        const obstacleRows = item.obstruction.length + dormerPoints.length + 1;
        minY = Math.min(minY, ySpace - (obstacleRows * cellHeight));
        maxX = Math.max(maxX, tableStartX + obstacleTableWidth);
        ySpace -= obstacleOffset;

        if (item.obstruction.length > 0) {
            TotalObsTable(item.obstruction, tableStartX, ySpace, cellHeight, totalObsWidths, Block);
            minY = Math.min(minY, ySpace - (4 * cellHeight));
            maxX = Math.max(maxX, tableStartX + totalObsWidth);
        }
    }

    return {
        minX: tableStartX,
        maxX,
        minY,
        maxY: Math.max(polyData.maxY + 40, headerAnchorY),
        width: maxX - tableStartX,
        height: (polyData.maxY + 40) - minY,
        headerAnchorY,
    };
}

export function drawBaseline2dShapes(dxf, item, Block, segmentIndex, options = {}) {
    const { highestEdges = [], layerName = BASELINE_DIMENSIONS_LAYER } = options;
    const measurement = buildBaselineMeasurementData(item, highestEdges);
    const diagram = drawBaselineDiagram(dxf, item, Block, measurement, layerName, segmentIndex);
    drawBaselineTables(item, measurement.rows, diagram, Block, layerName);
    return diagram;
}

function drawBaselineDiagram(dxf, item, Block, measurement, layerName, segmentIndex) {
    const pointsFt = Array.isArray(measurement?.alignedPointsFt) ? measurement.alignedPointsFt : [];
    if (!pointsFt.length) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const rp = pointsFt[0];
    const normalizePoint = (point) => feetPointToInches({ x: point.x - rp.x, y: point.y - rp.y });
    const points = pointsFt.map(normalizePoint);
    const highestEdges = (measurement.alignedHighestEdgesFt || []).map((edge) => ({
        a: normalizePoint(edge.a),
        b: normalizePoint(edge.b),
    }));
    const obstacles = (measurement.alignedObstaclesFt || []).map((obstacle) => obstacle.map(normalizePoint));
    const setback = Array.isArray(measurement?.alignedSetbackFt)
        ? measurement.alignedSetbackFt.map(normalizePoint)
        : null;
    const xEntries = (measurement.xEntries || []).map((entry) => ({
        value: entry.value,
        point: normalizePoint(entry.point),
    }));
    const yEntries = (measurement.yEntries || []).map((entry) => ({
        value: entry.value,
        point: normalizePoint(entry.point),
    }));

    const allDiagramPoints = [
        ...points,
        ...highestEdges.flatMap((edge) => [edge.a, edge.b]),
        ...obstacles.flatMap((obstacle) => obstacle),
        ...(Array.isArray(setback) ? setback : []),
    ];
    const bounds = getPointBounds(allDiagramPoints);
    const topReserve = 26 + xEntries.length * 12;
    const rightReserve = 28 + yEntries.length * 16;
    const leftReserve = 14;
    const bottomReserve = 16;
    const shiftX = leftReserve - bounds.minX;
    const shiftY = topReserve - bounds.minY;

    const shiftPoint = (point) => ({ x: point.x + shiftX, y: point.y + shiftY });
    const shiftedPoints = points.map(shiftPoint);
    const shiftedHighestEdges = highestEdges.map((edge) => ({ a: shiftPoint(edge.a), b: shiftPoint(edge.b) }));
    const shiftedObstacles = obstacles.map((obstacle) => obstacle.map(shiftPoint));
    const shiftedSetback = Array.isArray(setback) ? setback.map(shiftPoint) : null;
    const shiftedXEntries = xEntries.map((entry) => ({ ...entry, point: shiftPoint(entry.point) }));
    const shiftedYEntries = yEntries.map((entry) => ({ ...entry, point: shiftPoint(entry.point) }));

    const shiftedBounds = getPointBounds([
        ...shiftedPoints,
        ...shiftedHighestEdges.flatMap((edge) => [edge.a, edge.b]),
        ...shiftedObstacles.flatMap((obstacle) => obstacle),
        ...(Array.isArray(shiftedSetback) ? shiftedSetback : []),
    ]);
    const rpPoint = shiftedPoints[0];
    const topBase = shiftedBounds.maxY + 8;
    const rightBase = shiftedBounds.maxX + 16;

    const polygon = Block.addLWPolyline(shiftedPoints.map(toVertex2d), { flags: LWPolylineFlags.Closed });
    polygon.colorNumber = 8;
    polygon.layerName = layerName;

    if (Array.isArray(shiftedSetback) && shiftedSetback.length >= 3) {
        const setbackLine = Block.addLWPolyline(shiftedSetback.map(toVertex2d), { flags: LWPolylineFlags.Closed });
        setbackLine.colorNumber = 32;
        setbackLine.layerName = layerName;
    }

    shiftedObstacles.forEach((obstacle, obstacleIndex) => {
        const obstacleLine = Block.addLWPolyline(obstacle.map(toVertex2d), { flags: LWPolylineFlags.Closed });
        obstacleLine.colorNumber = 232;
        obstacleLine.layerName = layerName;
        const obstacleCenter = getPolygonCenter(obstacle);
        addFreeLabel(
            Block,
            `O${obstacleIndex + 1}`,
            5,
            232,
            point3d(obstacleCenter.x, obstacleCenter.y + 4, 0),
            { horizontal: 1, vertical: 2 }
        );
    });

    shiftedHighestEdges.forEach((edge, edgeIndex) => {
        const line = Block.addLine(point3d(edge.a.x, edge.a.y, 0), point3d(edge.b.x, edge.b.y, 0));
        line.colorNumber = 90;
        line.layerName = layerName;

        const mid = {
            x: (edge.a.x + edge.b.x) / 2,
            y: (edge.a.y + edge.b.y) / 2,
        };
        addFreeLabel(
            Block,
            `R${edgeIndex + 1}`,
            5,
            90,
            point3d(mid.x, mid.y + 4, 0),
            { horizontal: 1, vertical: 2 }
        );
    });

    shiftedPoints.forEach((point, index) => {
        if (index === 0) return;
        addFreeLabel(
            Block,
            String(index),
            5.5,
            4,
            point3d(point.x + 4, point.y + 4, 0),
            { horizontal: 1, vertical: 2 }
        );
    });
    addFreeLabel(
        Block,
        "RP",
        6,
        4,
        point3d(rpPoint.x + 5, rpPoint.y + 5, 0),
        { horizontal: 1, vertical: 2 }
    );

    shiftedXEntries.forEach((entry, level) => {
        const dimY = topBase + level * 12;
        addLayeredLine(Block, rpPoint.x, rpPoint.y, rpPoint.x, dimY, layerName, 252);
        addLayeredLine(Block, entry.point.x, entry.point.y, entry.point.x, dimY, layerName, 252);
        addLayeredLine(Block, rpPoint.x, dimY, entry.point.x, dimY, layerName, 252);
        addFreeLabel(
            Block,
            formatBaselineDistance(entry.value),
            4.5,
            252,
            point3d((rpPoint.x + entry.point.x) / 2, dimY + 3, 0),
            { horizontal: 1, vertical: 2 }
        );
    });

    shiftedYEntries.forEach((entry, level) => {
        const dimX = rightBase + level * 16;
        addLayeredLine(Block, rpPoint.x, rpPoint.y, dimX, rpPoint.y, layerName, 252);
        addLayeredLine(Block, entry.point.x, entry.point.y, dimX, entry.point.y, layerName, 252);
        addLayeredLine(Block, dimX, rpPoint.y, dimX, entry.point.y, layerName, 252);
        addFreeLabel(
            Block,
            formatBaselineDistance(entry.value),
            4.5,
            252,
            point3d(dimX + 4, (rpPoint.y + entry.point.y) / 2, 0),
            { horizontal: 0, vertical: 2 }
        );
    });

    const headerAnchorY = getSegmentHeaderAnchorY(item, shiftedBounds.maxY + 34);
    SegmentDetailLabels(Block, shiftedBounds.minX, shiftedBounds.maxY + 34, item.detail, item.label);

    addCircleMarker(
        Block,
        "polygon",
        shiftedPoints.map((point) => ({ point: point2d(point.x, point.y) })),
        layerName
    );

    return {
        minX: shiftedBounds.minX,
        maxX: shiftedBounds.maxX + rightReserve,
        minY: shiftedBounds.minY - bottomReserve,
        maxY: Math.max(shiftedBounds.maxY + topReserve + 34, headerAnchorY),
        headerAnchorY,
        };
}

function drawBaselineTables(item, rows, diagramBounds, Block, layerName) {
    const cellHeight = 12;
    const cellWidths = [28, 42, 42, 48, 48];
    const tableStartX = diagramBounds.minX;
    const tableStartY = diagramBounds.minY - 34;
    BaselineDetailsTable(item.label, rows, tableStartX, tableStartY, cellHeight, cellWidths, Block);
}

function mergeBounds(...boundsList) {
    const validBounds = boundsList.filter(
        (bounds) =>
            bounds &&
            Number.isFinite(bounds.minX) &&
            Number.isFinite(bounds.maxX) &&
            Number.isFinite(bounds.minY) &&
            Number.isFinite(bounds.maxY)
    );

    if (!validBounds.length) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
    }

    const minX = Math.min(...validBounds.map((bounds) => bounds.minX));
    const maxX = Math.max(...validBounds.map((bounds) => bounds.maxX));
    const minY = Math.min(...validBounds.map((bounds) => bounds.minY));
    const maxY = Math.max(...validBounds.map((bounds) => bounds.maxY));

    return {
        minX,
        maxX,
        minY,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function getSegmentHeaderAnchorY(item, startY) {
    const detailCount = Object.values(item?.detail || {}).filter((value) => value != null).length;
    return startY + (detailCount * 19.5) + 2;
}

function addLayeredLine(Block, x1, y1, x2, y2, layerName, colorNumber = 7) {
    const line = Block.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0));
    line.layerName = layerName;
    line.colorNumber = colorNumber;
    return line;
}

function toVertex2d(point) {
    return { point: point2d(point.x, point.y) };
}

function getPointBounds(points) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    points.forEach((point) => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
    });
    return { minX, maxX, minY, maxY };
}

function getPolygonCenter(points) {
    const count = Math.max(1, points.length);
    return points.reduce(
        (acc, point) => ({
            x: acc.x + point.x / count,
            y: acc.y + point.y / count,
        }),
        { x: 0, y: 0 }
    );
}

// Draws any polygon-like shape (panel, setback, obstacle, etc.) into the given block
export function drawShapeToBlock(dxf, polygon2d, Block, options = {}, index, segmentIndex, buttonFlag) {
    const {
        addPointLabel = false,
        addMeasurementLabel = false,
        addCircle = false,
        isObs = false,
        addName = false,
        shape = "polygon",
        layerName = null,
    } = options;

    // Convert to inches and project points
    const sourcePoints = Array.isArray(polygon2d) ? polygon2d : [];
    const inchesPoints = sourcePoints
        .filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y))
        .map((point) => projectTo2DFeetInches(point))
        .filter((point) => Number.isFinite(point.point?.x) && Number.isFinite(point.point?.y));

    if (inchesPoints.length < 3) {
        return inchesPoints;
    }
    
    // Draw polyline based on shape type
    let line = Block.addLWPolyline(inchesPoints, { flags: LWPolylineFlags.Closed });
    if (layerName) {
        line.layerName = layerName;
    }

    // Set line color and linetype based on shape
    if (shape === 'obstacle') {
        line.colorNumber = 232;
    } else if (shape === 'dormer') {
        line.colorNumber = 32;
        // line.lineType = 'ACAD_ISO11W600';
    } else if (shape === "setback") {
        line.colorNumber = 32;
    } else if (shape === "panel") {
        line.colorNumber = Colors.Blue;
    }

    // Add optional labels and measurements
    if (addPointLabel) {
        addPointLabels(inchesPoints, Block);
    }

    if (addMeasurementLabel) {
        addMeasurementLable(polygon2d, inchesPoints, Block);
    }

    if (addName) {
        addNameLabel(inchesPoints, Block, index, isObs);
    }

    // Add marker circles directly. Blocks and INSERTs are unnecessary for a
    // single circle and make the final DXF less portable across CAD readers.
    if (addCircle) {
        addCircleMarker(Block, shape, inchesPoints, layerName);
    }

    return inchesPoints;
}

function addCircleMarker(Block, shape, inchesPoints, layerName) {
    const firstPoint = inchesPoints[0]?.point;
    if (!firstPoint) return;
    const circle = Block.addCircle(
        point3d(firstPoint.x, firstPoint.y, 0),
        shape === "dormer" ? 1.2 : 5
    );
    if (layerName) {
        circle.layerName = layerName;
    }
}
